import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { License } from '@ps/web-core-enums';
import type { Account } from '@ps/web-core-store';
import { format, parseISO } from 'date-fns';

import { composeValidatorsWithThreshold } from '../composeValidatorsWithThreshold/composeValidatorsWithThreshold';
import { mustContainLowerCaseLetter } from '../mustContainLowerCaseLetter/mustContainLowerCaseLetter';
import { mustContainNumber } from '../mustContainNumber/mustContainNumber';
import { mustContainOnlyEnglishCharacters } from '../mustContainOnlyEnglishCharacters/mustContainOnlyEnglishCharacters';
import { mustContainSymbol } from '../mustContainSymbol/mustContainSymbol';
import { mustContainUpperCaseLetter } from '../mustContainUpperCaseLetter/mustContainUpperCaseLetter';
import { mustNotContain } from '../mustNotContain/mustNotContain';
import { mustNotContainName } from '../mustNotContainName/mustNotContainName';
import { mustNotContainSymbols } from '../mustNotContainSymbols/mustNotContainSymbols';
import { mustStartWithLetter } from '../mustStartWithLetter/mustStartWithLetter';
import { poBoxAddressNotAllowed } from '../poBoxAddressNotAllowed/poBoxAddressNotAllowed';
import { ADDRESS_NOT_ALLOWED_SYMBOLS } from '../utils/regex';

import {
  TXTCLI_Incorrect_Information_Entered,
  TXTCLI_Your_name_and_date_of_birth_is_ELL_1,
  TXTWCL_At_least_one_capital_letter_XA_ELL,
  TXTWCL_At_least_one_symbol_XeXgX_XX,
  TXTWCL_Must_contain_at_least_3_of_the_ELL,
  TXTWCL_Must_contain_at_least_one_digi_ELL,
  TXTWCL_The_address_you_have_entered_i_ELL_1
} from './getValidatorsForLicense.messages';

export interface ValidatorMapPayload {
  account: Partial<Account>;
  isRealMoneyOK: boolean;
}

const validatorMap: {
  [field in 'password' | 'userId' | 'address']: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [license in Sites | License | 'default']?: any;
  };
} = {
  password: {
    [Sites.SPA]: (payload?: ValidatorMapPayload) => {
      const validations = [
        mustContainSymbol(TXTWCL_At_least_one_symbol_XeXgX_XX),
        mustContainUpperCaseLetter(TXTWCL_At_least_one_capital_letter_XA_ELL)
      ];

      if (payload?.isRealMoneyOK) {
        const { dob } = payload.account;
        validations.push(
          composeValidatorsWithThreshold(
            [
              mustNotContain(dob && format(parseISO(dob), 'ddLLyy')),
              mustNotContainName(payload.account)
            ],
            TXTCLI_Your_name_and_date_of_birth_is_ELL_1
          )
        );
      }

      return validations;
    },
    [Sites.DE]: (payload?: ValidatorMapPayload) => {
      const validations = [
        composeValidatorsWithThreshold(
          [
            mustContainLowerCaseLetter(),
            mustContainUpperCaseLetter(),
            mustStartWithLetter(),
            mustContainNumber(TXTWCL_Must_contain_at_least_one_digi_ELL),
            mustContainSymbol()
          ],
          TXTWCL_Must_contain_at_least_3_of_the_ELL,
          3
        ),
        mustContainOnlyEnglishCharacters()
      ];

      if (payload?.isRealMoneyOK) {
        const { dob } = payload.account;
        validations.push(
          composeValidatorsWithThreshold(
            [
              mustNotContain(dob && format(parseISO(dob), 'ddLLyy')),
              mustNotContainName(payload.account)
            ],
            TXTCLI_Your_name_and_date_of_birth_is_ELL_1
          )
        );
      }

      return validations;
    },
    default: [mustContainNumber(TXTWCL_Must_contain_at_least_one_digi_ELL)]
  },
  userId: {
    [Sites.SPA]: () => [mustContainOnlyEnglishCharacters()],
    default: []
  },
  address: {
    [Sites.US_NJ]: () => [poBoxAddressNotAllowed],
    [Sites.CZE]: () => [
      mustNotContainSymbols(ADDRESS_NOT_ALLOWED_SYMBOLS, TXTCLI_Incorrect_Information_Entered)
    ],
    default: [
      mustNotContainSymbols(
        ADDRESS_NOT_ALLOWED_SYMBOLS,
        TXTWCL_The_address_you_have_entered_i_ELL_1
      )
    ]
  }
};

export const getValidatorsForLicense = (
  license: Sites | License,
  field: keyof typeof validatorMap,
  payload?: ValidatorMapPayload
) =>
  validatorMap[field][license]
    ? validatorMap[field][license](payload)
    : validatorMap[field].default;
